/**
 * Accueil personnel (composition ScreenHome du design) : salutation + « Personnaliser », HomeGrid de cartes choisies,
 * ordonnées et masquées par l'utilisateur (préférences synchronisées). Grille de cases régulières façon iOS : chaque
 * carte occupe un nombre entier de cases, un trou est comblé dès qu'une carte y tient (placerCases). Mode
 * personnalisation : les cartes ondulent, « − » retire, on les
 * déplace en les faisant glisser (les autres s'écartent, voir grilleAccueil.ts), « + Ajouter une carte » ouvre le
 * catalogue des cartes masquées.
 * Indicateurs : GET /v1/accueil?filiale= (fournis par les modules actifs pour la filiale, libellés accueil.indicateurs.<id>).
 * Météo : GET /v1/meteo (CarteMeteo.tsx : position de l'utilisateur, sinon ville de la filiale).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FILIALES, MODES_WIDGET, TOUTES_FILIALES, aDesModes, changerModeWidget, modeWidget, noeud as noeudPartage, ordonnerWidgets, placerCases, type AccueilReponse,
  type Indicateur, type MetierId, type ModeWidget, type WidgetAccueil, type WidgetAModes, cleLibre,
} from '@agora/shared';
import {
  Button, HomeGrid, KpiTile, NewsList, QuickLinks, RecentModules, RecentStack, SegmentedControl, Skeleton, SkeletonGroup, SocialFeed, StatusBadge,
  Widget, WorldClocks,
} from '../../design';
import { ErreurApi, useApi, useFiliale, useFormats, useMoi, usePreferences, useT } from '../../socle';
import { useSocle } from '../contexte';
import { LINKEDIN, OBJETS_RECENTS, actualitesDatees } from '../donnees-exemple';
import { LogoProdeval } from '../shell/BarreLaterale';
import { nomFiliale } from '../shell/Entete';
import { CarteMeteo } from './CarteMeteo';
import { useColonnes, useGrilleTriable, useHauteursCases } from './grilleAccueil';

/** Cartes sur deux colonnes (bureau et tablette) ; les autres occupent une colonne. */
const CARTES_LARGES: readonly WidgetAccueil[] = ['objetsRecents', 'actualite', 'linkedin'];

/** Chemin lisible d'un nœud : « Service › Hotline ». */
export function cheminNoeud(id: string, lib: (id: string) => string): string {
  const n = noeudPartage(id);
  return n ? [...n.parents].reverse().map(lib).join(' › ') : '';
}

/** Choix compact / moyen / complet d'une carte (mode personnalisation de l'accueil, Réglages). */
export function ChoixModeCarte({ id }: { id: WidgetAModes }) {
  const { t } = useT();
  const { preferences, modifier } = usePreferences();
  return (
    <SegmentedControl
      ariaLabel={t('accueil.modeAffichage', { carte: t(`accueil.widgets.${id}`) })} value={modeWidget(preferences, id)}
      options={MODES_WIDGET.map((m) => ({ value: m, label: t(`accueil.modes.${m}`), title: t(`accueil.modesAide.${id}.${m}`) }))}
      onChange={(v) => modifier((p) => changerModeWidget(p, id, v as ModeWidget))}
    />
  );
}

export function Accueil() {
  const { t, lib, locale } = useT();
  const f = useFormats();
  const moi = useMoi();
  const { filiale, info } = useFiliale();
  const { preferences, modifier } = usePreferences();
  const { noeuds } = useSocle();
  const [edition, setEdition] = React.useState(false);
  const [catalogueCartes, setCatalogueCartes] = React.useState(false);
  const [annonce, setAnnonce] = React.useState('');

  const api = useApi();
  const qAccueil = useQuery({
    queryKey: ['accueil', filiale],
    queryFn: ({ signal }) => api.get<AccueilReponse>('/v1/accueil', { filiale }, signal),
    staleTime: 60_000,
    placeholderData: (precedent) => precedent,
  });

  const exemple = <StatusBadge status="neutral" label={t('commun.donneesExemple')} />;
  const visibles = preferences.accueil.widgets.filter((w) => w.visible).map((w) => w.id);
  const masquees = preferences.accueil.widgets.filter((w) => !w.visible).map((w) => w.id);

  const changerVisibilite = (id: WidgetAccueil, visible: boolean) =>
    modifier((p) => ({ ...p, accueil: { widgets: p.accueil.widgets.map((w) => (w.id === id ? { ...w, visible } : w)) } }));
  const titreCarte = (id: WidgetAccueil) => t(`accueil.widgets.${id}`);
  const commun = (id: WidgetAccueil) => ({
    editing: edition, removeLabel: t('accueil.retirerCarte'), onRemove: () => changerVisibilite(id, false),
    moveLabel: t('accueil.deplacerCarte', { carte: titreCarte(id) }),
  });

  // Grille : ordre provisoire pendant un déplacement, enregistré quand la carte est posée.
  const colonnes = useColonnes();
  const tri = useGrilleTriable<WidgetAccueil>({
    ordre: visibles, actif: edition,
    enregistrer: (ordre) => modifier((p) => ordonnerWidgets(p, ordre)),
    annoncer: (etape, id, position, total) => setAnnonce(t(`accueil.grille.${etape}`, { carte: titreCarte(id), position, total })),
  });
  const hauteurs = useHauteursCases(tri.grille);
  const indicateurs = qAccueil.data?.indicateurs || [];
  /**
   * Taille en cases. Largeur : 1 ou 2 colonnes (sur téléphone, seules les petites cartes restent côte à côte) ;
   * hauteur : mesurée sur le contenu ; indicateurs : une case par tuile.
   */
  const taille = (id: WidgetAccueil) => {
    if (id === 'indicateurs' && indicateurs.length) {
      const l = Math.min(indicateurs.length, colonnes.colonnes);
      return { id, l, h: Math.ceil(indicateurs.length / l), mesure: false };
    }
    const petite = aDesModes(id) && modeWidget(preferences, id) === 'compact';
    const l = colonnes.mobile ? (petite ? 1 : 2) : Math.min(CARTES_LARGES.includes(id) ? 2 : 1, colonnes.colonnes);
    return { id, l, h: hauteurs[id] ?? 2, mesure: true };
  };
  const tailles = new Map(tri.ordre.map((id) => [id, taille(id)]));
  const places = placerCases([...tailles.values()], colonnes.colonnes);
  const enCase = (id: WidgetAccueil, contenu: React.ReactNode) => {
    const k = tailles.get(id)!, p = places.get(id)!;
    return (
      <div
        key={id} {...tri.propsCase(id)} data-mesure={k.mesure ? '' : undefined}
        style={{ '--agx-col': p.colonne + 1, '--agx-rang': p.rangee + 1, '--agx-l': k.l, '--agx-h': k.h } as React.CSSProperties}
      >
        <div className="agx-case-contenu">{contenu}</div>
      </div>
    );
  };

  /** Localité de l'utilisateur : filiale active, sinon sa première filiale (heure en mode compact). */
  const filialeLocale = info || FILIALES.find((x) => moi.filiales.includes(x.code)) || FILIALES[0];
  const mesFiliales = FILIALES.filter((x) => moi.filiales.includes(x.code));
  const aujourdhui = new Date();
  const date = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(aujourdhui);

  const recents = preferences.recents.filter((r) => noeuds.has(r.id)).map((r) => {
    const n = noeuds.get(r.id)!;
    return { id: r.id, label: lib(r.id), path: cheminNoeud(r.id, lib) || t('nav.referentiels'), metier: (n.metier || 'it') as MetierId, href: n.route };   // sans « quand » (demande du 22/10/2026)
  });
  const favoris = preferences.favoris.filter((id) => noeuds.has(id)).map((id) => {
    const n = noeuds.get(id)!;
    return { id, label: lib(id), path: cheminNoeud(id, lib), icon: n.icone, metier: (n.metier || 'it') as MetierId, href: n.route };
  });

  /** Indicateurs : une tuile par case, déplacées ensemble (une seule carte « Mes indicateurs » dans l'ordre). */
  const caseIndicateurs = (id: WidgetAccueil) => {
    if (!indicateurs.length) {
      // Chargement : squelette d'un indicateur (valeur, variation) ; sinon erreur ou absence d'indicateur.
      const texte = qAccueil.error ? t(`erreurs.${qAccueil.error instanceof ErreurApi ? qAccueil.error.code : 'interne'}`) : t('accueil.aucunIndicateur');
      return enCase(id, (
        <Widget title={t('accueil.widgets.indicateurs')} {...commun(id)}>
          {qAccueil.isLoading ? (
            <SkeletonGroup label={t('app.chargement')}>
              <Skeleton variant="title" width={45} />
              <Skeleton width={30} />
            </SkeletonGroup>
          ) : <p className="agx-vide" role="status">{texte}</p>}
        </Widget>
      ));
    }
    return enCase(id, (
      <div className="agx-case-groupe">
        {indicateurs.map((k: Indicateur) => (
          <Widget key={`kpi-${k.id}`} title={t(cleLibre('accueil.indicateurs', k.id))} link={k.route ? t('commun.voir') : undefined} href={k.route} {...commun(id)}>
            <KpiTile
              label="" value={f.nombre(k.valeur)} unit={k.unite}
              delta={k.variation === undefined ? undefined : `${k.variation > 0 ? '+' : k.variation < 0 ? '−' : '±'}${f.nombre(Math.abs(k.variation))}`}
              tone="flat"
            />
          </Widget>
        ))}
      </div>
    ));
  };

  const carte = (id: WidgetAccueil): React.ReactNode => {
    switch (id) {
      case 'objetsRecents':
        return (
          <Widget key={id} title={t('accueil.widgets.objetsRecents')} link={t('accueil.historique')} extra={exemple} {...commun(id)}>
            <RecentStack items={OBJETS_RECENTS.map((o, i, l) => (i === l.length - 1 ? { ...o, time: f.relatif(new Date(Date.now() - 4 * 60_000)) } : o))} />
          </Widget>
        );
      case 'meteo':
        return (
          <CarteMeteo
            key={id} mode={modeWidget(preferences, id)}
            widget={{
              title: t('accueil.widgets.meteo'), ...commun(id),
              tools: <ChoixModeCarte id={id} />, toolsLabel: t('accueil.reglerCarte', { carte: titreCarte(id) }),
            }}
          />
        );
      case 'horloges': {
        // compact : ma filiale ; moyen : mes filiales, lignes resserrées ; complet : toutes les filiales
        const mode = modeWidget(preferences, id);
        const villes = mode === 'complet' ? FILIALES : mode === 'moyen' && mesFiliales.length ? mesFiliales : [filialeLocale];
        return (
          <Widget
            key={id} title={t('accueil.widgets.horloges')} {...commun(id)}
            tools={<ChoixModeCarte id={id} />} toolsLabel={t('accueil.reglerCarte', { carte: titreCarte(id) })}
          >
            <WorldClocks
              live locale={locale} sameLabel={t('accueil.ici')} homeZone={Intl.DateTimeFormat().resolvedOptions().timeZone} dense={mode === 'moyen'}
              clocks={villes.map((x) => ({ label: x.ville, timeZone: x.fuseau, country: x.pays }))}
            />
          </Widget>
        );
      }
      case 'utilisesRecemment':
        return (
          <Widget key={id} title={t('accueil.widgets.utilisesRecemment')} {...commun(id)}>
            {recents.length ? <RecentModules items={recents} /> : <p className="agx-vide">{t('accueil.aucunRecent')}</p>}
          </Widget>
        );
      case 'accesRapides':
        return (
          <Widget key={id} title={t('accueil.widgets.accesRapides')} {...commun(id)}>
            {favoris.length ? <QuickLinks compact items={favoris} /> : <p className="agx-vide">{t('accueil.aucunFavori')}</p>}
          </Widget>
        );
      case 'actualite':
        return (
          <Widget key={id} title={t('accueil.widgets.actualite')} link={t('actualite.toutes')} extra={exemple} {...commun(id)}>
            <NewsList featured items={actualitesDatees(locale, 'court')} />
          </Widget>
        );
      case 'linkedin':
        return (
          <Widget key={id} title={t('accueil.widgets.linkedin')} link={t('accueil.voirPage')} extra={exemple} {...commun(id)}>
            <SocialFeed
              // eslint-disable-next-line no-restricted-syntax -- noms propres (marque, réseau social), identiques dans toutes les langues
              author="PRODEVAL" avatar={<LogoProdeval hauteur={10} />} sourceLabel="LinkedIn" openLabel={t('accueil.voirPost')}
              posts={LINKEDIN.map((p) => ({ ...p, time: f.relatif(new Date(Date.now() - p.jours * 86_400_000)) }))}
            />
          </Widget>
        );
      default:
        return null;
    }
  };
  const caseCarte = (id: WidgetAccueil) => (id === 'indicateurs' ? caseIndicateurs(id) : enCase(id, carte(id)));

  return (
    <>
      <div className="ag-home-head">
        <div>
          <h1>{t('accueil.bonjour', { prenom: moi.prenom || moi.email })}</h1>
          <p>
            {edition ? t('accueil.grille.aide')
              : t('accueil.sousTitre', { date: date.charAt(0).toUpperCase() + date.slice(1), filiale: filiale === TOUTES_FILIALES ? t('entete.toutesFiliales') : nomFiliale(filiale, t) })}
          </p>
        </div>
        <Button variant={edition ? 'primary' : 'secondary'} icon={edition ? 'check' : 'edit'} onClick={() => { setEdition((e) => !e); setCatalogueCartes(false); }}>
          {edition ? t('accueil.terminer') : t('accueil.personnaliser')}
        </Button>
      </div>
      <HomeGrid
        cells gridRef={tri.grille}
        editing={edition}
        addLabel={t('accueil.ajouterCarte')}
        // Catalogue des cartes masquées : menu déroulant ouvert par la tuile « + Ajouter une carte »
        addMenu={{
          title: t('reglages.cartesAccueil'), ariaLabel: t('accueil.ajouterCarte'), open: catalogueCartes, onOpenChange: setCatalogueCartes,
          items: masquees.length
            ? masquees.map((id) => ({ id, label: t(`accueil.widgets.${id}`), icon: 'plus' as const }))
            : [{ id: '-', label: t('commun.aucunResultat'), disabled: true }],
          onSelect: (id) => changerVisibilite(id as WidgetAccueil, true),
        }}
      >
        {tri.ordre.map(caseCarte)}
      </HomeGrid>
      <div className="agx-annonce" role="status" aria-live="polite">{annonce}</div>
    </>
  );
}
